import { useState, useEffect, useRef } from 'react';
import { 
  processPrompt, 
  hasUserApiKey,
  createTask, 
  updateLeadStatusViaAI, 
  sendSMSViaAI 
} from '../lib/gemini';
import { 
  Bot, User, Send, Target, Sparkles, Check, Trash2, 
  UserPlus, Loader2, AlertTriangle, 
  RefreshCw, Smartphone, Search, X, ArrowDown,
  Mic, Pin, Edit2, Settings, Settings2, Copy, CheckCircle2,
  Plus, MessageSquare, History, ChevronLeft, ChevronRight,
  Zap, Brain, Save
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useStore, AI_PLAN_LIMITS } from '../store/useStore';
import { FormattedContent } from '../components/FormattedContent';
import { ConfirmModal } from '../components/ConfirmModal';
import { RateLimitModal } from '../components/RateLimitModal';
import { parseIntent } from '../lib/ai/intent-parser';
import { actionHandlers } from '../lib/ai/action-handlers';
import { formatTemplate } from '../lib/ai/template-engine';
import { intents } from '../lib/ai/intents';
import { AI_PROVIDERS } from '../lib/ai/types';
import {
  isUrl, scrapePropertyUrl, parseLeadFromText,
  formatLeadSummary, getMissingFields,
  type ParsedLeadData
} from '../lib/ai/lead-importer';

interface AIBotMessage {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: string;
  intent?: string;
  data?: any;
  systemLog?: string;
}

// FormattedContent moved to shared component

export default function AITest() {
  const { 
    dataLoaded, leads, currentUser, 
    incrementAiUsage,
    aiThreads, currentAiThreadId, aiMessages,
    createAiThread, deleteAiThread, setCurrentAiThread,
    updateAiThreadTitle, toggleAiThreadPin, addAiMessage, clearAiThreadMessages,
    aiName, setAiName, aiModel, preferred_api_provider, setPreferredApiProvider,
    aiCustomPrompt, setAiCustomPrompt,
    aiMemories, setAiMemories,
    aiTone, setAiTone, aiUserAlias, setAiUserAlias, syncSettingsToSupabase,
    isAiGenerating, setAiGenerating, aiDisplayedText, setAiDisplayedText,
    typingMessageId: storeTypingMessageId, setTypingMessageId: setStoreTypingMessageId,
    refreshCredits
  } = useStore();

  // Local memory helpers
  const addMemory = (text: string) => {
    if (!text.trim()) return;
    setAiMemories([...aiMemories, text.trim()]);
    toast.success('Memory synchronized');
  };

  const removeMemory = (index: number) => {
    const newMemories = aiMemories.filter((_, i) => i !== index);
    setAiMemories(newMemories);
    toast.success('Memory purged');
  };

  
  const [debug, setDebug] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasKey, setHasKey] = useState<boolean | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  
  const [pendingAction, setPendingAction] = useState<{
    intent: string;
    data: any;
    response: string;
  } | null>(null);
  const [leadSearch, setLeadSearch] = useState('');
  const [rateLimit, setRateLimit] = useState<{ seconds: number; originalPrompt: string } | null>(null);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Thread Sidebar Search & Editing
  const [threadSearch, setThreadSearch] = useState('');
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: React.ReactNode;
    onConfirm: () => void;
    variant?: 'primary' | 'danger';
    confirmLabel?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Guided Action Wizards State
  const [activeWizard, setActiveWizard] = useState<'lead' | 'sms' | 'task' | null>(null);
  const [wizardData, setWizardData] = useState<any>({});
  const [showSettings, setShowSettings] = useState(false);

  // Pending lead import (awaiting user confirmation "yes"/"no")
  const [pendingLeadImport, setPendingLeadImport] = useState<ParsedLeadData | null>(null);

  // Voice Input State
  const [isRecording, setIsRecording] = useState(false);
  const [replyTo, setReplyTo] = useState<AIBotMessage | null>(null);
  const recognitionRef = useRef<any>(null);

  // Derive messages from current thread
  const messages = (currentAiThreadId ? aiMessages[currentAiThreadId] : []) || [];

  // Clear pending prompt on load (Part 1 Fix)
  useEffect(() => {
    localStorage.removeItem('ai_pending_prompt');
    document.title = `WholeScale OS`;
    // Refresh credits so the correct reset_at (timezone-aware) is loaded immediately
    refreshCredits();
  }, [aiName]);

  // Initialize threads if empty
  useEffect(() => {
    if (!dataLoaded) return;
    if (aiThreads.length === 0) {
      createAiThread('Main Conversation');
    } else if (!currentAiThreadId) {
      setCurrentAiThread(aiThreads[0].id);
    }
  }, [dataLoaded, aiThreads, currentAiThreadId]);



  useEffect(() => {
    const handleSettingsUpdate = () => {
      setRateLimit(null);
    };
    window.addEventListener('ai-settings-updated', handleSettingsUpdate);
    return () => window.removeEventListener('ai-settings-updated', handleSettingsUpdate);
  }, [currentUser]);



  useEffect(() => {
    if (hasKey === null) {
      async function checkKey() {
        const keyExists = await hasUserApiKey();
        setHasKey(keyExists);
      }
      checkKey();
    }
  }, [hasKey]);

  useEffect(() => {
    const savedExpiry = localStorage.getItem('ai_rate_limit_expiry');
    if (savedExpiry && Date.now() > parseInt(savedExpiry)) {
      setRateLimit(null);
      localStorage.removeItem('ai_rate_limit_expiry');
      localStorage.removeItem('ai_rate_limit_prompt');
    }
  }, []);

  // Clear rate limit when provider or model changes
  useEffect(() => {
    setRateLimit(null);
    localStorage.removeItem('ai_rate_limit_expiry');
    localStorage.removeItem('ai_rate_limit_prompt');
  }, [preferred_api_provider, aiModel]);

  useEffect(() => {
    let interval: any;
    if (rateLimit && rateLimit.seconds > 0) {
      interval = setInterval(() => {
        setRateLimit(prev => prev ? { ...prev, seconds: prev.seconds - 1 } : null);
      }, 1000);
    } else if (rateLimit && rateLimit.seconds <= 0) {
      setRateLimit(null);
      localStorage.removeItem('ai_rate_limit_expiry');
    }
    return () => clearInterval(interval);
  }, [rateLimit?.seconds]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setDebug(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Sync AI settings to Supabase
  useEffect(() => {
    if (currentUser) {
      syncSettingsToSupabase();
    }
  }, [aiName, aiModel, preferred_api_provider, aiCustomPrompt, aiMemories, aiTone, aiUserAlias]);

  // Typing Animation logic moved to handleSendMessage

  // Scroll to bottom on thread load and on initial page mount
  // Fix 1: Proper scroll to bottom logic
  useEffect(() => {
    const scrollToBottom = () => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
            console.log('[OSBot] Scrolled to bottom (Thread/Message Update)');
          }
        }, 50);
      });
    };
    scrollToBottom();
  }, [messages.length, currentAiThreadId]);

  // Mount useEffect for initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
        console.log('[OSBot] Scrolled to bottom (Initial Mount)');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const pushMessage = (msg: Omit<AIBotMessage, 'id' | 'timestamp'>) => {
    if (!currentAiThreadId) return null;
    const id = (Date.now() + Math.random()).toString();
    addAiMessage(currentAiThreadId, {
      ...msg,
      id,
      timestamp: new Date().toISOString()
    });
    return id;
  };

  useEffect(() => {
    // Initialize Speech Recognition
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;

      recognitionRef.current.onresult = (event: any) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setPrompt(prev => prev + ' ' + transcript);
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };
    }
  }, []);

  useEffect(() => {
    if (autoScrollEnabled && (isAiGenerating || messages.length > 0)) {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      });
    }
  }, [messages, aiDisplayedText, isAiGenerating, autoScrollEnabled]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    setAutoScrollEnabled(isAtBottom);
    setShowScrollButton(!isAtBottom && scrollHeight > clientHeight);
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const handleSendMessage = async (e?: React.FormEvent, customPrompt?: string) => {
    // Fix 2: Set interaction flag at the very start
    const { setHasInteractedWithAI } = useStore.getState();
    setHasInteractedWithAI(true);
    
    if (e) e.preventDefault();
    const text = customPrompt || prompt;
    if (!text.trim() || loading || !currentAiThreadId || !hasKey) return;

    // ── OS Bot: Handle pending lead confirmation ─────────────────────────────
    const isYes = /^(yes|yeah|yep|yup|sure|ok|okay|create|confirm|do it|add it|save it)$/i.test(text.trim().replace(/[!.?]$/, ''));
    const isNo = /^(no|nope|cancel|skip|nevermind|never mind|don't|stop|ignore)$/i.test(text.trim().replace(/[!.?]$/, ''));

    if (pendingLeadImport) {
      if (isYes) {
        pushMessage({ role: 'user', content: text.trim() });
        setPrompt('');
        try {
          const { addLead } = (await import('../store/useStore')).useStore.getState();
          addLead({
            name: pendingLeadImport.name || 'Unknown Owner',
            email: pendingLeadImport.email || '',
            phone: pendingLeadImport.phone || '',
            propertyAddress: pendingLeadImport.propertyAddress || '',
            city: pendingLeadImport.city || '',
            state: pendingLeadImport.state || '',
            zip: pendingLeadImport.zip || '',
            estimatedValue: pendingLeadImport.estimatedValue || 0,
            bedrooms: pendingLeadImport.bedrooms || 0,
            bathrooms: pendingLeadImport.bathrooms || 0,
            sqft: pendingLeadImport.sqft || 0,
            propertyType: (pendingLeadImport.propertyType as any) || 'single-family',
            status: 'new',
            source: 'other',
            agentName: pendingLeadImport.agentName || '',
            agentPhone: pendingLeadImport.agentPhone || '',
            notes: pendingLeadImport.notes || '',
            offerAmount: 0, lat: 0, lng: 0,
            assignedTo: '', probability: 40,
            engagementLevel: 3, timelineUrgency: 3, competitionLevel: 3, documents: []
          });
          pushMessage({
            role: 'ai',
            content: `✅ **Lead created!** I've added **${pendingLeadImport.name || pendingLeadImport.propertyAddress || 'the property'}** to your leads. You can find it in the Leads section.`,
            systemLog: '🤖 OS Bot · Lead Import'
          });
          toast.success(`Lead created: ${pendingLeadImport.name || pendingLeadImport.propertyAddress || 'New Lead'}`);
          incrementAiUsage(1);
        } catch (err) {
          console.error('Lead creation failed:', err);
          pushMessage({ role: 'ai', content: '❌ Failed to create lead. Please try again or use the Import page.', systemLog: '🤖 OS Bot' });
        }
        setPendingLeadImport(null);
        return;
      } else if (isNo) {
        pushMessage({ role: 'user', content: text.trim() });
        setPrompt('');
        pushMessage({ role: 'ai', content: 'Got it — lead creation cancelled. Let me know if you need anything else.', systemLog: '🤖 OS Bot' });
        setPendingLeadImport(null);
        return;
      }
      // If they said something else, clear pending and continue with normal flow
      setPendingLeadImport(null);
    }

    // ── OS Bot: Detect URL → scrape and create lead ──────────────────────────
    if (isUrl(text.trim())) {
      pushMessage({ role: 'user', content: text.trim() });
      setPrompt('');
      setLoading(true);
      pushMessage({ role: 'ai', content: '🔍 Scraping property data from that URL, one moment...', systemLog: '🤖 OS Bot · Scraper' });
      try {
        const result = await scrapePropertyUrl(text.trim());
        if (result.success && result.data) {
          const lead = result.data;
          const raw = result.rawData as any;
          const missing = getMissingFields(lead);
          const summary = formatLeadSummary(lead, missing);
          // Prepend what was found
          const foundParts: string[] = [];
          if (raw?.source) foundParts.push(`Source: **${raw.source}**`);
          if (lead.propertyAddress) foundParts.push(`Address: **${lead.propertyAddress}**`);
          if (lead.estimatedValue) foundParts.push(`Listed at: **$${lead.estimatedValue.toLocaleString()}**`);
          if (lead.bedrooms || lead.bathrooms) foundParts.push(`**${lead.bedrooms || '?'} bed / ${lead.bathrooms || '?'} bath**`);
          if (lead.sqft) foundParts.push(`**${lead.sqft.toLocaleString()} sqft**`);
          const foundMsg = foundParts.length > 0 ? `✅ Found: ${foundParts.join(' · ')}\n\n` : '';
          pushMessage({
            role: 'ai',
            content: foundMsg + summary,
            systemLog: '🤖 OS Bot · URL Import'
          });
          setPendingLeadImport(lead);
          incrementAiUsage(2);
        } else {
          pushMessage({
            role: 'ai',
            content: `⚠️ ${result.errorMsg}\n\nYou can also paste the property details as text and I'll extract the lead info for you.`,
            systemLog: '🤖 OS Bot · Scraper'
          });
        }
      } catch (err) {
        pushMessage({ role: 'ai', content: '❌ Scraping failed unexpectedly. Try pasting the property details as text instead.', systemLog: '🤖 OS Bot' });
      } finally {
        setLoading(false);
      }
      return;
    }

    // ── OS Bot: Detect pasted property text ─────────────────────────────────
    // Heuristic: text has 3+ lines OR contains address-like patterns + price/beds/phone
    const lines = text.trim().split(/\n/);
    const hasPropertySignals = (
      (text.match(/\$[\d,kKmM]+/) || text.match(/\d+\s*(bed|bath|sq)/i)) &&
      (text.match(/\b\d{1,6}\s+[A-Za-z].*(?:St|Rd|Ave|Blvd|Dr|Ln|Ct|Way|Pl)/i) ||
       text.match(/\b\(\d{3}\)|\d{3}[-.]\d{3}[-.]\d{4}/))
    );
    const isMultiLinePaste = lines.length >= 3 && text.length > 60;

    if (hasPropertySignals || isMultiLinePaste) {
      const parsed = parseLeadFromText(text.trim());
      const hasAnyData = parsed.propertyAddress || parsed.name || parsed.phone || parsed.estimatedValue;

      if (hasAnyData) {
        pushMessage({ role: 'user', content: text.trim() });
        setPrompt('');
        setLoading(true);
        const missing = getMissingFields(parsed);
        const summary = formatLeadSummary(parsed, missing);
        pushMessage({
          role: 'ai',
          content: `📋 I found lead information in your message:\n\n${summary}`,
          systemLog: '🤖 OS Bot · Smart Paste'
        });
        setPendingLeadImport(parsed);
        incrementAiUsage(1);
        setLoading(false);
        return;
      }
    }

    pushMessage({ role: 'user', content: text.trim() });
    setPrompt('');
    setLoading(true); // Fix duplicate responses by locking input immediately
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // 1. Try Local Rule-Based AI first (No Credits)
      const matched = parseIntent(text.trim());
      if (matched) {
        const handler = (actionHandlers as any)[matched.intent.action];
        if (handler) {
          const res = await handler(matched.params);
          if (res.success) {
            const formatted = formatTemplate(matched.intent.template, res.data);
            const aiId = pushMessage({ 
              role: 'ai', 
              content: formatted, 
              intent: matched.intent.name,
              data: res.data,
              systemLog: "🤖 OS Bot"
            });
            if (aiId) setStoreTypingMessageId(aiId);
            incrementAiUsage(1); // Deduct 1 credit for local AI response
            setLoading(false);
            return;
          }
        }
      }

      // 2. Fallback to Gemini AI
      const response = await processPrompt(text.trim(), {
        threadId: currentAiThreadId,
        messages: messages.map(m => ({ role: m.role, content: m.content })),
        replyTo: replyTo ? { role: replyTo.role, content: replyTo.content } : null,
        currentTime: new Date().toISOString(),
        source: 'user'
      }, preferred_api_provider || aiModel, controller.signal);
      
      setReplyTo(null); // Clear reply after sending
      
      if (response.intent === 'rate_limit') {
        const retry = response.data?.retryAfter || 60;
        setRateLimit({ seconds: retry, originalPrompt: text });
        setShowRateLimitModal(true);
        localStorage.setItem('ai_rate_limit_expiry', (Date.now() + retry * 1000).toString());
        pushMessage({ role: 'ai', content: `⚠️ Rate Limit Hit: ${response.response}` });
      } else {
        let clean = response.response || '';
        if (typeof clean === 'string' && clean.trim().startsWith('{')) {
          try {
            const p = JSON.parse(clean);
            if (p.response) clean = p.response;
          } catch (e) {}
        }

        const getProvider = (m: string) => {
          const lower = m.toLowerCase();
          if (lower.includes('gemini')) return 'gemini';
          if (lower.includes('claude')) return 'claude';
          if (lower.includes('gpt')) return 'openai';
          if (lower.includes('deepseek')) return 'deepseek';
          return m.split('-')[0] || m;
        };
        const modelLabel = response.systemLog || `🤖 OS Bot · ${getProvider(aiModel)}`;
        const aiId = pushMessage({ 
          role: 'ai', 
          content: clean, 
          intent: response.intent, 
          systemLog: modelLabel 
        });
        
        // Typing animation start
        setAiGenerating(true);
        setStoreTypingMessageId(aiId || null);
        
        let currentText = "";
        const words = clean.split(" ");
        for (let i = 0; i < words.length; i++) {
          currentText += (i === 0 ? "" : " ") + words[i];
          setAiDisplayedText(currentText);
          await new Promise(r => setTimeout(r, 20));
        }
        
        setAiGenerating(false);
        setStoreTypingMessageId(null);
        setAiDisplayedText('');

        if (response.intent === 'fallback_local') {
          toast.error(`Cloud AI Error: ${response.response}. Switching to Local AI.`);
          const handler = (actionHandlers as any)['capabilities'];
          if (handler) {
            const fallbackRes = await handler({});
            const template = intents.find((i: any) => i.name === 'capabilities')?.template || '{response}';
            const formatted = formatTemplate(template, fallbackRes.data);

            setTimeout(() => {
              pushMessage({ 
                role: 'ai', 
                content: formatted, 
                intent: 'capabilities',
                systemLog: "🤖 Local Fallback Executed" 
              });
            }, 500);
          }
        }

        if (response.intent === 'confirm_action' && response.data) {
          setConfirmModal({
            isOpen: true,
            title: 'Confirm AI Action',
            message: response.response,
            onConfirm: () => executeAction(response.data.intent || 'unknown', response.data),
            confirmLabel: 'Confirm & Execute',
            variant: 'primary'
          });
        } else if (['update_status', 'update_lead', 'delete_lead', 'send_sms'].includes(response.intent)) {
          console.log(`AI Intent [${response.intent}] flagged for confirmation.`);
          setPendingAction({ intent: response.intent, data: response.data, response: clean });
          if (response.data?.target) setLeadSearch(response.data.target);
        } else {
          // Execute directly if no manual confirmation needed (for basic intents)
          executeAction(response.intent, response.data);
        }

        if (messages.length <= 2) {
          updateAiThreadTitle(currentAiThreadId, text.substring(0, 30) + (text.length > 30 ? '...' : ''));
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        pushMessage({ role: 'ai', content: "Request cancelled by user.", intent: 'error' });
      } else {
        pushMessage({ role: 'ai', content: "Error communicating with the API.", intent: 'error' });
      }
    } finally {
      setLoading(false);
      setAiGenerating(false);
      setStoreTypingMessageId(null);
      abortControllerRef.current = null;
    }
  };

  const handleStopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const executeAction = async (intent: string, data: any, confirmedLeadId?: string) => {
    let log = undefined;
    const finalData = confirmedLeadId ? { ...data, leadId: confirmedLeadId } : data;
    try {
      if (intent === 'create_task' && finalData) {
        createTask(finalData);
        log = `System: Created task '${finalData.title}'`;
      } else if (intent === 'update_status' && finalData?.leadId) {
        const res = await updateLeadStatusViaAI(finalData.leadId, finalData.newStatus);
        log = `System: ${res.message}`;
      } else if (intent === 'send_sms' && (finalData?.target || finalData?.leadId) && finalData?.message) {
        setLoading(true);
        const res = await sendSMSViaAI(
          confirmedLeadId || finalData.leadId || finalData.target, 
          finalData.message,
          finalData.targetCarrier
        );
        log = `System: ${res.message}`;
        if (!res.success) {
          pushMessage({ role: 'ai', content: `❌ SMS Failed: ${res.message}`, intent: 'error' });
          return;
        }
      }

      // ONLY push a new message if there is a system log to show, 
      // otherwise we already pushed the AI response in handleSendMessage.
      if (log) {
        const sysId = pushMessage({ role: 'ai', content: log, intent, data: finalData, systemLog: log });
        if (sysId) setStoreTypingMessageId(sysId);
      }
    } catch (err) {
      console.error('Action failed:', err);
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  };

  const handleTestConnection = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // Use callExternalAPI directly to bypass local intent interception for 'ping'
      const { callExternalAPI } = await import('../lib/ai/api-router');
      const testPrompt = "Respond with exactly 'OS Bot is connected and online.'";
      const result = await callExternalAPI(testPrompt, { 
        provider: aiModel,
        skipReflection: true
      });
      const responseText = typeof result === 'string' ? result : result?.response || '';
      pushMessage({ role: 'ai', content: `Test: ${responseText}`, intent: 'test' });
    } catch (err: any) {
      pushMessage({ role: 'ai', content: `Test failed: ${err.message}`, intent: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    if (currentAiThreadId) clearAiThreadMessages(currentAiThreadId);
  };

  // Calculate time until reset (Unified with Widget logic)
  // Timer: use credits_reset_at (UTC ISO string already corrected by refreshCredits)
  // Don't use setHours() which applies browser timezone - just diff against now directly.
  const now = new Date();
  const resetDate = currentUser?.credits_reset_at ? new Date(currentUser.credits_reset_at) : null;
  const msRem = resetDate ? Math.max(0, resetDate.getTime() - now.getTime()) : 0;
  const hRem = Math.floor(msRem / (1000 * 60 * 60));
  const mRem = Math.floor((msRem % (1000 * 60 * 60)) / (1000 * 60));
  const resetTimer = resetDate ? `${hRem}h ${mRem}m` : '...';

  return (
    <div className="flex h-full overflow-hidden">
      {/* Thread Sidebar */}
      <aside 
        className={`${isSidebarOpen ? 'w-64' : 'w-0'} bg-[var(--t-surface)] border-r border-[var(--t-border)] transition-all duration-300 flex flex-col overflow-hidden relative shrink-0`}
      >
        <div className="p-4 border-b border-[var(--t-border)] flex items-center justify-between">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <History size={16} style={{ color: 'var(--t-primary)' }} />
            History
          </h2>
          <button 
            onClick={() => createAiThread()}
            className="p-1.5 rounded-lg transition-colors"
            style={{ background: 'var(--t-primary-dim)', color: 'var(--t-primary)' }}
            title="New Conversation"
          >
            <Plus size={16} />
          </button>
        </div>

        <div className="px-3 pt-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)] w-3 h-3" />
            <input 
              type="text"
              value={threadSearch}
              onChange={(e) => setThreadSearch(e.target.value)}
              placeholder="Search threads..."
              className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-lg pl-8 pr-3 py-1.5 text-[10px] text-white outline-none placeholder:text-[var(--t-text-muted)] transition-all font-mono"
              style={{ 
                // @ts-expect-error custom prop
                '--tw-ring-color': 'var(--t-primary-dim)' 
              }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {aiThreads
            .filter(t => t.title.toLowerCase().includes(threadSearch.toLowerCase()))
            .map((t) => (
            <div 
              key={t.id}
              className="group flex items-center gap-2 p-3 rounded-xl transition-all cursor-pointer relative"
              style={currentAiThreadId === t.id ? { background: 'var(--t-primary-dim)', color: 'white' } : { color: 'var(--t-text-muted)' }}
              onClick={() => setCurrentAiThread(t.id)}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {t.pinned ? (
                  <Pin size={12} className="shrink-0" style={{ color: 'var(--t-primary)', fill: 'var(--t-primary)' }} />
                ) : (
                  <MessageSquare size={14} style={currentAiThreadId === t.id ? { color: 'var(--t-primary)' } : { color: 'var(--t-text-muted)' }} />
                )}
                
                <div className="flex-1 min-w-0">
                  {editingThreadId === t.id ? (
                    <input
                      autoFocus
                      className="w-full bg-[var(--t-background)] border rounded px-1 py-0.5 text-xs text-white outline-none"
                      style={{ borderColor: 'var(--t-primary-dim)' }}
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={() => {
                        if (editingTitle.trim()) updateAiThreadTitle(t.id, editingTitle.trim());
                        setEditingThreadId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (editingTitle.trim()) updateAiThreadTitle(t.id, editingTitle.trim());
                          setEditingThreadId(null);
                        }
                        if (e.key === 'Escape') setEditingThreadId(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <p className="text-xs font-medium truncate">{t.title}</p>
                      <p className="text-[10px] text-[var(--t-text-muted)]">{new Date(t.lastMessageAt).toLocaleDateString()}</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={(e) => { e.stopPropagation(); toggleAiThreadPin(t.id); }}
                  className="p-1 transition-colors"
                  style={t.pinned ? { color: 'var(--t-primary)' } : { color: 'var(--t-text-muted)' }}
                  title={t.pinned ? "Unpin" : "Pin"}
                >
                  <Pin size={12} className={t.pinned ? "fill-current" : ""} style={t.pinned ? { fill: 'var(--t-primary)' } : {}} />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); setEditingThreadId(t.id); setEditingTitle(t.title); }}
                  className="p-1 hover:text-white transition-colors"
                  style={{ color: 'var(--t-text-muted)' }}
                  title="Rename"
                >
                  <Edit2 size={12} />
                </button>
                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setConfirmModal({
                      isOpen: true,
                      title: 'Delete Thread',
                      message: `Are you sure you want to delete "${t.title}"? This action cannot be undone.`,
                      confirmLabel: 'Delete Thread',
                      variant: 'danger',
                      onConfirm: () => deleteAiThread(t.id)
                    });
                  }}
                  className="p-1 hover:text-[var(--t-error)] transition-colors text-[var(--t-text-muted)]"
                  title="Delete"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
          {aiThreads.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-xs text-[var(--t-text-muted)] italic">No threads yet</p>
            </div>
          )}
        </div>
      </aside>

      {/* Toggle Sidebar Button */}
      <button 
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="absolute left-[240px] top-1/2 -translate-y-1/2 z-10 w-4 h-12 bg-[var(--t-surface)] border border-[var(--t-border)] border-l-0 rounded-r-lg flex items-center justify-center text-[var(--t-text-muted)] hover:text-white transition-all overflow-hidden"
        style={{ left: isSidebarOpen ? '256px' : '0' }}
      >
        {isSidebarOpen ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
      </button>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 p-4" style={{ background: 'var(--t-background)' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b shrink-0" style={{ borderColor: 'var(--t-border)', opacity: 0.8 }}>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--t-text)' }}>
              <Sparkles className="w-5 h-5" style={{ color: 'var(--t-primary)' }} />
              {aiName}
              <div className="ml-2 flex items-center gap-2 px-2 py-1 rounded-lg border" style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}>
                <div className="text-[9px] font-medium uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>Credits</div>
                <div className="text-xs font-bold" style={{ color: 'var(--t-primary)' }}>
                  {currentUser?.credits_remaining ?? AI_PLAN_LIMITS[currentUser?.subscriptionTier || 'Free'] ?? 50}/{AI_PLAN_LIMITS[currentUser?.subscriptionTier || 'Free'] || 50}
                </div>
                <div className="h-3 w-[1px] bg-[var(--t-border)] mx-1 opacity-50" />
                <div className="text-[9px] text-[var(--t-text-muted)] font-medium">
                  Resets in {resetTimer}
                </div>
              </div>
            </h1>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={handleTestConnection}
              disabled={loading}
              className="text-[10px] px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5"
              style={{ background: 'var(--t-primary-dim)', color: 'var(--t-primary-text)' }}
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Test
            </button>
            <button 
              onClick={clearHistory}
              className="text-[10px] px-3 py-1.5 rounded-full transition-colors border"
              style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)', color: 'var(--t-text-secondary)' }}
            >
              Clear
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`text-[10px] px-3 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${
                showSettings ? 'bg-[var(--t-primary)] text-white' : 'border bg-[var(--t-surface)]'
              }`}
              style={{ 
                borderColor: 'var(--t-border)', 
                color: showSettings ? 'white' : 'var(--t-text-secondary)' 
              }}
            >
              <Settings size={12} className={showSettings ? 'rotate-90' : ''} />
              Settings
            </button>
          </div>
        </div>
        {/* Quick Settings Panel */}
        {showSettings && (
          <div className="mb-6 p-6 rounded-3xl bg-[var(--t-surface)] border border-[var(--t-border)] shadow-2xl animate-in slide-in-from-top-4 duration-500 backdrop-blur-xl">
             <div className="flex items-center justify-between mb-6">
                <div className="flex flex-col">
                  <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-[var(--t-primary)] flex items-center gap-2">
                    <Settings2 size={14} className="animate-spin-slow" /> Intelligence Hub
                  </h3>
                  <p className="text-[9px] text-[var(--t-text-muted)] mt-1 font-medium">Neural configuration and identity settings</p>
                </div>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="p-2 hover:bg-black/10 rounded-full transition-colors"
                >
                  <X size={16} className="text-[var(--t-text-muted)]" />
                </button>
             </div>
             
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                
                {/* Column 1: Identity & Persona */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 mb-2 border-b border-[var(--t-border)] pb-2">
                    <Bot size={14} className="text-[var(--t-primary)]" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text)]">Identity</span>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">Bot Display Name</label>
                      <input
                        type="text"
                        value={aiName}
                        onChange={(e) => setAiName(e.target.value)}
                        className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-xs text-[var(--t-text)] focus:ring-1 focus:ring-[var(--t-primary)] outline-none transition-all shadow-sm"
                        placeholder="Bot Name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">User Nickname</label>
                      <input
                        type="text"
                        value={aiUserAlias}
                        onChange={(e) => setAiUserAlias(e.target.value)}
                        className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-xs text-[var(--t-text)] focus:ring-1 focus:ring-[var(--t-primary)] outline-none transition-all shadow-sm"
                        placeholder="How it calls you"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">Interaction Tone</label>
                      <select
                        value={aiTone}
                        onChange={(e) => setAiTone(e.target.value)}
                        className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-xs text-[var(--t-text)] focus:ring-1 focus:ring-[var(--t-primary)] outline-none cursor-pointer shadow-sm"
                      >
                        {['Professional', 'Friendly', 'Direct', 'Sassy', 'Aggressive', 'Humorous', 'Custom'].map(tone => (
                          <option key={tone} value={tone}>{tone}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Column 2: Intelligence & Models */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 mb-2 border-b border-[var(--t-border)] pb-2">
                    <Zap size={14} className="text-yellow-500" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text)]">Intelligence</span>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">Active Processor</label>
                      <select
                        value={preferred_api_provider || aiModel}
                        onChange={(e) => setPreferredApiProvider(e.target.value)}
                        className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-xs text-[var(--t-text)] focus:ring-1 focus:ring-[var(--t-primary)] outline-none cursor-pointer shadow-sm"
                      >
                        <optgroup label="Local (Fast & Private)">
                          <option value="local">Local OS Bot Engine (No Credits)</option>
                        </optgroup>
                        <optgroup label="Neural Cloud (Requires Keys)">
                          {AI_PROVIDERS.filter(p => p.id !== 'local').map(provider => {
                            const profileKey = currentUser?.user_api_keys?.[provider.id];
                            const localKey = localStorage.getItem(`user_${provider.id === 'claude' ? 'anthropic' : provider.id}_api_key`) || 
                                             localStorage.getItem(`user_${provider.id}_key`) ||
                                             localStorage.getItem('user_ai_api_key');
                            const hasKey = !!(profileKey || localKey);
                            return (
                              <option key={provider.id} value={provider.id} disabled={!hasKey}>
                                {provider.name} {hasKey ? '✓' : '(Key Missing)'}
                              </option>
                            );
                          })}
                        </optgroup>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">Global Behavior</label>
                      <textarea
                        value={aiCustomPrompt}
                        onChange={(e) => setAiCustomPrompt(e.target.value)}
                        placeholder="Define core bot behavior rules..."
                        className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-xs text-[var(--t-text)] focus:ring-1 focus:ring-[var(--t-primary)] outline-none min-h-[100px] resize-none shadow-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Column 3: Neural Cache & Sync */}
                <div className="space-y-6">
                   <div className="flex items-center justify-between mb-2 border-b border-[var(--t-border)] pb-2">
                    <div className="flex items-center gap-2">
                      <Brain size={14} className="text-purple-500" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text)]">Neural Cache</span>
                    </div>
                    <button 
                      onClick={() => {
                        const text = window.prompt('Enter a new memory:');
                        if (text) addMemory(text);
                      }}
                      className="p-1 rounded-md bg-[var(--t-primary)]/10 text-[var(--t-primary)] hover:bg-[var(--t-primary)] hover:text-white transition-all shadow-sm"
                      title="Add Memory"
                    >
                      <Plus size={10} />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div className="max-h-48 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                      {aiMemories.length > 0 ? aiMemories.map((m: string, idx: number) => (
                        <div key={idx} className="group flex items-center justify-between gap-2 p-3 rounded-xl bg-[var(--t-bg)] border border-[var(--t-border)] hover:border-[var(--t-primary)]/30 transition-all shadow-sm">
                          <p className="text-[10px] text-[var(--t-text)] leading-tight flex-1">{m}</p>
                          <button 
                            onClick={() => removeMemory(idx)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-[var(--t-error)] hover:bg-[var(--t-error)]/10 rounded-md transition-all"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      )) : (
                        <div className="text-center py-8 px-4 border border-dashed border-[var(--t-border)] rounded-2xl">
                          <p className="text-[10px] text-[var(--t-text-muted)] italic">No active neural memories</p>
                        </div>
                      )}
                    </div>
                    
                    <button 
                      onClick={async () => {
                        await syncSettingsToSupabase();
                        toast.success('Neural profile synced ✓', { duration: 2000 });
                      }}
                      className="w-full py-3 mt-2 flex items-center justify-center gap-2 bg-[var(--t-primary)] text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-xl shadow-lg hover:shadow-[var(--t-primary)]/20 active:scale-95 transition-all"
                    >
                      <Save size={12} /> Sync Neural Profile
                    </button>
                  </div>
                </div>
             </div>
          </div>
        )}
        {/* Chat Messages */}
        <div 
          ref={containerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto mb-4 rounded-2xl border p-4 space-y-6 relative"
          style={{ background: 'var(--t-surface-hover)', borderColor: 'var(--t-border)', opacity: 0.9 }}
        >
          {messages.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center opacity-40">
              <Bot size={48} className="mb-4 animate-pulse" style={{ color: 'var(--t-primary)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--t-text)' }}>How can I help you with your real estate OS today?</p>
            </div>
          )}
          {messages.map((msg: AIBotMessage) => (
            <div key={msg.id} className={`flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 border shadow-sm transition-all overflow-hidden" 
                style={{ 
                  background: msg.role === 'user' ? 'var(--t-primary)' : '#8b5cf6',
                  borderColor: msg.role === 'user' ? 'var(--t-primary)' : '#7c3aed',
                  boxShadow: msg.role === 'ai' ? '0 0 15px rgba(139, 92, 246, 0.4)' : 'none'
                }}>
                {msg.role === 'user' ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
              </div>
              
              {/* Message Body */}
              <div className={`flex flex-col max-w-[80%] ${msg.role === 'user' ? 'items-end' : 'items-start'} group/msg relative`}>
                <div className={`px-4 py-3 rounded-2xl ${
                  msg.role === 'user' 
                    ? 'rounded-tr-none text-white' 
                    : 'border rounded-tl-none'
                }`} style={{ 
                  background: msg.role === 'user' ? 'var(--t-primary)' : 'var(--t-surface)',
                  borderColor: msg.role === 'user' ? 'transparent' : 'var(--t-border)',
                  color: msg.role === 'user' ? 'var(--t-on-primary)' : 'var(--t-text)'
                }}>
                  {/* Message Actions */}
                  <div className={`absolute top-2 ${msg.role === 'user' ? '-left-12' : '-right-12'} flex gap-1 opacity-0 group-hover/msg:opacity-100 transition-opacity z-10`}>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(msg.content);
                        setCopiedId(msg.id);
                        setTimeout(() => setCopiedId(null), 2000);
                        toast.success('Copied to clipboard');
                      }}
                      className="p-1.5 rounded-lg bg-[var(--t-surface)] border border-[var(--t-border)] text-[var(--t-text-muted)] hover:text-[var(--t-primary)] transition-colors shadow-sm"
                      title="Copy message"
                    >
                      {copiedId === msg.id ? <CheckCircle2 size={12} className="text-[var(--t-success)]" /> : <Copy size={12} />}
                    </button>
                    <button 
                      onClick={() => setReplyTo(msg)}
                      className="p-1.5 rounded-lg bg-[var(--t-surface)] border border-[var(--t-border)] text-[var(--t-text-muted)] hover:text-[var(--t-primary)] transition-colors shadow-sm"
                      title="Reply with context"
                    >
                      <MessageSquare size={12} />
                    </button>
                    {msg.role === 'user' && (
                      <button 
                        onClick={() => setPrompt(msg.content)}
                        className="p-1.5 rounded-lg bg-[var(--t-surface)] border border-[var(--t-border)] text-[var(--t-text-muted)] hover:text-[var(--t-primary)] transition-colors shadow-sm"
                        title="Edit and resend"
                      >
                        <Edit2 size={12} />
                      </button>
                    )}
                  </div>

                  <div className="text-sm leading-relaxed">
                    {msg.id === storeTypingMessageId ? (
                      <div className="flex flex-col gap-3">
                        <FormattedContent content={aiDisplayedText} />
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--t-primary)]/10 rounded-full w-fit border border-[var(--t-primary)]/20 shadow-sm">
                          <span className="w-1.5 h-1.5 bg-[var(--t-primary)] rounded-full animate-bounce [animation-duration:0.8s]" />
                          <span className="w-1.5 h-1.5 bg-[var(--t-primary)] rounded-full animate-bounce [animation-delay:0.2s] [animation-duration:0.8s]" />
                          <span className="w-1.5 h-1.5 bg-[var(--t-primary)] rounded-full animate-bounce [animation-delay:0.4s] [animation-duration:0.8s]" />
                        </div>
                      </div>
                    ) : (
                      <FormattedContent content={msg.content} />
                    )}
                  </div>
                </div>
                
                {debug && msg.role === 'ai' && msg.intent && !['error', 'unknown', 'ask_question'].includes(msg.intent) && (
                  <div className="flex gap-2 items-center mt-2 px-1">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border" 
                      style={{ background: 'var(--t-primary-dim)', color: 'var(--t-primary-text)', borderColor: 'var(--t-primary-dim)' }}>
                      {msg.intent}
                    </span>
                  </div>
                )}
                
                {msg.systemLog && (
                  <div className="mt-2 border rounded-lg p-2 text-xs font-mono w-full text-left"
                    style={{ background: 'var(--t-surface-active)', borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}>
                    {msg.systemLog}
                  </div>
                )}
                
                <span className="text-[10px] mt-1 px-1" style={{ color: 'var(--t-text-muted)' }}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>


              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 border border-[#7c3aed] shadow-[0_0_15px_rgba(139, 92, 246, 0.4)]"
                style={{ background: '#8b5cf6' }}
              >
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="px-4 py-4 rounded-2xl bg-[var(--t-surface)] border border-[var(--t-border)] rounded-tl-none flex items-center gap-1.5">
                <div className="w-2 h-2 bg-[var(--t-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-[var(--t-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-[var(--t-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
          
          {/* Scroll to Bottom Button */}
          {showScrollButton && (
            <button
              onClick={() => bottomRef.current?.scrollIntoView({ behavior: 'auto' })}
              className="absolute bottom-4 right-4 p-3 rounded-full bg-[var(--t-primary)] text-white shadow-[0_0_20px_rgba(var(--t-primary-rgb),0.4)] transition-all hover:scale-110 active:scale-95 animate-in fade-in slide-in-from-bottom-4 z-[100] flex items-center justify-center border border-white/20"
              title="Scroll to bottom"
            >
              <ArrowDown size={20} />
            </button>
          )}
        </div>

        {/* Rate Limit Banner */}
        {rateLimit && (
          <div className="mb-4 border rounded-2xl p-4 flex gap-4 items-center animate-in fade-in slide-in-from-bottom-2 shrink-0"
            style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--t-error)' }}>
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--t-text)' }}>Rate Limit Reached</h3>
              <p className="text-xs mt-1" style={{ color: 'var(--t-text-secondary)' }}>
                You've reached your rate limit for the OS Cloud API. 
                Retrying in <span className="font-bold" style={{ color: 'var(--t-primary)' }}>{rateLimit.seconds}s</span>...
                <br />
                <span className="text-[10px] mt-1 block">
                  Upgrade your Google Cloud/AI Studio plan for higher limits. 
                  <a href="https://ai.google.dev/gemini-api/docs/rate-limits" target="_blank" rel="noopener noreferrer" className="ml-1 underline transition-colors" style={{ color: 'var(--t-primary)' }}>
                    Increase Limits
                  </a>
                </span>
              </p>
            </div>
            <button disabled className="px-4 py-2 rounded-xl text-xs opacity-50 flex items-center gap-2" style={{ background: 'var(--t-background)', color: 'var(--t-text-muted)' }}>
              <RefreshCw className="w-3 h-3" /> Cooldown active
            </button>
          </div>
        )}

        {/* Input Area */}
        <div className={`p-4 rounded-2xl border transition-all shrink-0 ${rateLimit && !rateLimit.originalPrompt ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
          style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}>
          {replyTo && (
            <div className="mb-2 p-3 rounded-xl border flex items-start gap-3 animate-in slide-in-from-bottom-2 duration-200"
              style={{ background: 'var(--t-background)', borderColor: 'var(--t-primary-dim)' }}
            >
              <div className="shrink-0 mt-0.5">
                <MessageSquare size={14} style={{ color: 'var(--t-primary)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: 'var(--t-primary)' }}>
                  Replying to {replyTo.role === 'user' ? 'yourself' : 'OS Bot'}
                </p>
                <p className="text-xs text-[var(--t-text-muted)] line-clamp-2 italic">
                  "{replyTo.content}"
                </p>
              </div>
              <button 
                type="button"
                onClick={() => setReplyTo(null)}
                className="p-1 hover:bg-black/10 rounded-lg transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          )}
          
          <form onSubmit={handleSendMessage} className="flex gap-2 items-end relative">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder={rateLimit && rateLimit.seconds > 0 ? `Rate limit active... wait ${rateLimit.seconds}s` : (isRecording ? "Listening..." : "How can I help you today?")}
              className={`flex-1 border rounded-xl pl-4 pr-12 py-4 outline-none resize-none min-h-[100px] max-h-[300px] transition-all scrollbar-thin scrollbar-thumb-slate-700 ${isRecording ? 'shadow-xl' : ''}`}
              style={{ 
                background: 'var(--t-background)', 
                borderColor: isRecording ? 'var(--t-primary)' : 'var(--t-border)', 
                color: 'var(--t-text)',
                // @ts-expect-error custom css prop
                '--tw-shadow': isRecording ? '0 0 15px var(--t-primary-dim)' : 'none'
              }}
              rows={2}
              disabled={loading}
            />
            <button
              type="button"
              onClick={toggleRecording}
              className={`absolute right-16 bottom-[10px] p-2 rounded-lg transition-all ${isRecording ? 'animate-pulse' : 'hover:scale-110'}`}
              style={{ 
                color: isRecording ? 'var(--t-primary)' : 'var(--t-text-muted)',
                background: isRecording ? 'var(--t-primary-dim)' : 'transparent'
              }}
              title={isRecording ? "Stop Recording" : "Voice Typing"}
            >
              <Mic className="w-5 h-5" />
            </button>
            <button
              type={loading ? "button" : "submit"}
              onClick={loading ? handleStopGeneration : undefined}
              disabled={(!loading && !prompt.trim()) || !!(rateLimit && rateLimit.seconds > 0)}
              className="w-12 h-12 shrink-0 text-white rounded-xl flex items-center justify-center transition-all group shadow-lg active:scale-95 disabled:opacity-30 OSBot-send-button"
              style={{ background: loading ? 'var(--t-error)' : 'var(--t-primary)' }}
              title={loading ? "Stop Generating" : "Send Message"}
            >
              {loading ? (
                <X className="w-5 h-5 text-white animate-pulse" />
              ) : (
                <Send className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Lead Selection Modal */}
      {pendingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm ">
          <div className="border w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] animate-in zoom-in-95 duration-200"
            style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}
          >
            <div className="p-4 border-b flex items-center justify-between"
              style={{ background: 'var(--t-surface-hover)', borderColor: 'var(--t-border)' }}
            >
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Target className="w-5 h-5" style={{ color: 'var(--t-primary)' }} />
                  Confirm Lead for Action
                </h3>
                <p className="text-[10px] mt-0.5 uppercase tracking-wider font-bold" style={{ color: 'var(--t-text-muted)' }}>Intent: {pendingAction.intent.replace('_', ' ')}</p>
              </div>
              <button 
                onClick={() => { setPendingAction(null); setLoading(false); }}
                className="p-1.5 hover:bg-black/10 rounded-lg transition-colors"
                style={{ color: 'var(--t-text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 border-b" style={{ background: 'var(--t-background)', borderColor: 'var(--t-border)' }}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--t-text-muted)' }} />
                <input 
                  autoFocus
                  type="text"
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  placeholder="Search leads..."
                  className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm transition-all"
                  style={{ 
                    background: 'var(--t-input-bg)', 
                    borderColor: 'var(--t-border)', 
                    color: 'var(--t-text)',
                    // @ts-expect-error custom prop
                    '--tw-ring-color': 'var(--t-primary)'
                  }}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1" style={{ background: 'var(--t-background)' }}>
              {leads
                .filter(l => 
                  (l.name?.toLowerCase().includes(leadSearch.toLowerCase())) || 
                  (l.propertyAddress?.toLowerCase().includes(leadSearch.toLowerCase()))
                )
                .slice(0, 50)
                .map(lead => (
                  <button
                    key={lead.id}
                    onClick={() => {
                      setConfirmModal({
                        isOpen: true,
                        title: 'Confirm Action',
                        message: `Proceed with ${pendingAction.intent.replace('_', ' ')} for ${lead.name || lead.propertyAddress}?`,
                        onConfirm: () => executeAction(pendingAction.intent, pendingAction.data, lead.id),
                        confirmLabel: 'Yes, Proceed'
                      });
                    }}
                    className="w-full flex items-center justify-between p-3 rounded-xl transition-all group"
                    style={{ background: 'transparent' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs border"
                        style={{ background: 'var(--t-surface-hover)', borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}
                      >
                        {lead.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="text-sm font-semibold transition-colors" style={{ color: 'var(--t-text)' }}>
                          {lead.name || 'Unknown Lead'}
                        </p>
                        <p className="text-[10px] truncate max-w-[200px]" style={{ color: 'var(--t-text-muted)' }}>{lead.propertyAddress || 'No address'}</p>
                      </div>
                    </div>
                    <span className="text-[9px] uppercase font-black px-2 py-0.5 rounded-full"
                      style={{ 
                        background: String(lead.status) === 'new' ? 'var(--t-primary-dim)' : 
                                   String(lead.status) === 'closed-won' ? 'var(--t-success-dim)' : 'var(--t-surface-hover)',
                        color: String(lead.status) === 'new' ? 'var(--t-primary)' : 
                               String(lead.status) === 'closed-won' ? 'var(--t-success)' : 'var(--t-text-muted)'
                      }}
                    >
                      {String(lead.status)}
                    </span>
                  </button>
                ))
              }
            </div>

            <div className="p-4 border-t flex gap-3" style={{ background: 'var(--t-surface-hover)', borderColor: 'var(--t-border)' }}>
              <button 
                onClick={() => { setPendingAction(null); setLoading(false); }}
                className="flex-1 py-2.5 rounded-xl border text-xs font-bold uppercase tracking-wider transition-colors"
                style={{ borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setConfirmModal({
                    isOpen: true,
                    title: 'Confirm Action',
                    message: `Proceed with ${pendingAction.intent.replace('_', ' ')} without selecting a lead?`,
                    onConfirm: () => executeAction(pendingAction.intent, pendingAction.data),
                    confirmLabel: 'Yes, Proceed'
                  });
                }}
                className="flex-1 py-2.5 rounded-xl transition-colors text-xs font-bold uppercase tracking-wider"
                style={{ background: 'var(--t-secondary)', color: 'var(--t-on-secondary, white)' }}
              >
                Skip Lead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guided Action Wizards */}
      {activeWizard === 'lead' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm ">
          <div className="border w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
            style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}
          >
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--t-border)' }}>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5" style={{ color: 'var(--t-primary)' }} />
                Add New Lead
              </h3>
              <button 
                onClick={() => setActiveWizard(null)}
                className="p-1.5 hover:bg-black/10 rounded-lg transition-colors"
                style={{ color: 'var(--t-text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold ml-1" style={{ color: 'var(--t-text-muted)' }}>Full Name</label>
                <input 
                  type="text" 
                  placeholder="e.g. Jessica Taylor"
                  onChange={(e) => setWizardData({ ...wizardData, name: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 text-sm outline-none"
                  style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)', '--tw-ring-color': 'var(--t-primary)' } as any}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold ml-1" style={{ color: 'var(--t-text-muted)' }}>Phone</label>
                  <input 
                    type="tel" 
                    placeholder="555-0199"
                    onChange={(e) => setWizardData({ ...wizardData, phone: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)', '--tw-ring-color': 'var(--t-primary)' } as any}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold ml-1" style={{ color: 'var(--t-text-muted)' }}>Email</label>
                  <input 
                    type="email" 
                    placeholder="jessica@example.com"
                    onChange={(e) => setWizardData({ ...wizardData, email: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)', '--tw-ring-color': 'var(--t-primary)' } as any}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold ml-1" style={{ color: 'var(--t-text-muted)' }}>Property Address</label>
                <input 
                  type="text" 
                  placeholder="123 Main St, Anytown"
                  onChange={(e) => setWizardData({ ...wizardData, propertyAddress: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 text-sm outline-none"
                  style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)', '--tw-ring-color': 'var(--t-primary)' } as any}
                />
              </div>
            </div>
            <div className="p-4 border-t flex gap-3" style={{ background: 'var(--t-surface-hover)', borderColor: 'var(--t-border)' }}>
              <button 
                onClick={() => setActiveWizard(null)}
                className="flex-1 py-2 rounded-xl border text-xs font-bold transition-colors"
                style={{ borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  executeAction('create_lead', wizardData);
                  setActiveWizard(null);
                }}
                className="flex-1 py-2 rounded-xl text-white transition-colors text-xs font-bold"
                style={{ background: 'var(--t-primary)' }}
              >
                Create Lead
              </button>
            </div>
          </div>
        </div>
      )}

      {activeWizard === 'sms' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm ">
          <div className="border w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] animate-in zoom-in-95 duration-200"
            style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}
          >
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--t-border)' }}>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Smartphone className="w-5 h-5" style={{ color: 'var(--t-primary)' }} />
                Send SMS Message
              </h3>
              <button 
                onClick={() => setActiveWizard(null)}
                className="p-1.5 hover:bg-black/10 rounded-lg transition-colors"
                style={{ color: 'var(--t-text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 border-b" style={{ background: 'var(--t-background)', borderColor: 'var(--t-border)' }}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--t-text-muted)' }} />
                <input 
                  type="text"
                  placeholder="Search lead to text..."
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm outline-none"
                  style={{ 
                    background: 'var(--t-input-bg)', 
                    borderColor: 'var(--t-border)', 
                    color: 'var(--t-text)',
                    '--tw-ring-color': 'var(--t-primary)'
                  } as any}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1" style={{ background: 'var(--t-background)' }}>
              {leads
                .filter(l => l.name?.toLowerCase().includes(leadSearch.toLowerCase()) || l.phone?.includes(leadSearch))
                .slice(0, 10)
                .map(lead => (
                  <button
                    key={lead.id}
                    onClick={() => setWizardData({ ...wizardData, leadId: lead.id, leadName: lead.name })}
                    className="w-full flex items-center justify-between p-3 rounded-xl transition-all border"
                    style={{ 
                      background: wizardData.leadId === lead.id ? 'var(--t-primary-dim)' : 'transparent',
                      borderColor: wizardData.leadId === lead.id ? 'var(--t-primary)' : 'transparent'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-[10px]"
                        style={{ background: 'var(--t-surface-hover)', color: 'var(--t-text-muted)' }}
                      >
                        {lead.name?.charAt(0) || 'U'}
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-semibold" style={{ color: 'var(--t-text)' }}>{lead.name || 'Unknown'}</p>
                        <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{lead.phone || 'No phone'}</p>
                      </div>
                    </div>
                  </button>
                ))
              }
            </div>

            <div className="p-4 space-y-3 border-t" style={{ borderColor: 'var(--t-border)' }}>
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold ml-1" style={{ color: 'var(--t-text-muted)' }}>Message Content</label>
                <textarea 
                  rows={3}
                  placeholder={`Hi ${wizardData.leadName || 'there'}, ...`}
                  onChange={(e) => setWizardData({ ...wizardData, message: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 text-sm outline-none resize-none"
                  style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)', '--tw-ring-color': 'var(--t-primary)' } as any}
                />
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={() => setActiveWizard(null)}
                  className="flex-1 py-2 rounded-xl border text-xs font-bold transition-colors"
                  style={{ borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}
                >
                  Cancel
                </button>
                <button 
                  disabled={!wizardData.leadId || !wizardData.message}
                  onClick={() => {
                    executeAction('send_sms', { target: wizardData.leadId, message: wizardData.message });
                    setActiveWizard(null);
                  }}
                  className="flex-1 py-2 rounded-xl text-white transition-colors text-xs font-bold disabled:opacity-50"
                  style={{ background: 'var(--t-primary)' }}
                >
                  Send SMS
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeWizard === 'task' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm ">
          <div className="border w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
            style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}
          >
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--t-border)' }}>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Check className="w-5 h-5" style={{ color: 'var(--t-primary)' }} />
                Schedule New Task
              </h3>
              <button 
                onClick={() => setActiveWizard(null)}
                className="p-1.5 hover:bg-black/10 rounded-lg transition-colors"
                style={{ color: 'var(--t-text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold ml-1" style={{ color: 'var(--t-text-muted)' }}>Task Title</label>
                <input 
                  type="text" 
                  placeholder="e.g. Call John Smith"
                  onChange={(e) => setWizardData({ ...wizardData, title: e.target.value })}
                  className="w-full border rounded-xl px-4 py-2 text-sm outline-none"
                  style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)', '--tw-ring-color': 'var(--t-primary)' } as any}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold ml-1" style={{ color: 'var(--t-text-muted)' }}>Due Date</label>
                  <input 
                    type="date" 
                    onChange={(e) => setWizardData({ ...wizardData, dueDate: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)', '--tw-ring-color': 'var(--t-primary)' } as any}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold ml-1" style={{ color: 'var(--t-text-muted)' }}>Priority</label>
                  <select 
                    onChange={(e) => setWizardData({ ...wizardData, priority: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)', '--tw-ring-color': 'var(--t-primary)' } as any}
                  >
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex gap-3" style={{ background: 'var(--t-surface-hover)', borderColor: 'var(--t-border)' }}>
              <button 
                onClick={() => setActiveWizard(null)}
                className="flex-1 py-2 rounded-xl border text-xs font-bold transition-colors"
                style={{ borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  executeAction('create_task', wizardData);
                  setActiveWizard(null);
                }}
                className="flex-1 py-2 rounded-xl text-white transition-colors text-xs font-bold"
                style={{ background: 'var(--t-primary)' }}
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {debug && (
        <div className="fixed bottom-24 right-4 z-50 p-4 bg-[var(--t-surface)]/90 border border-[var(--t-border)] rounded-2xl text-[10px] font-mono backdrop-blur-xl shadow-2xl max-w-xs animate-in slide-in-from-right-4">
          <div className="flex items-center gap-2 font-bold mb-3 text-[var(--t-primary)] uppercase tracking-widest">
            <RefreshCw className="w-3 h-3 animate-spin-slow" /> DEBUG CONSOLE
          </div>
          <div className="space-y-1 text-[var(--t-text-muted)]">
            <div className="flex justify-between"><span>Thread ID:</span> <span className="text-white truncate max-w-[120px]">{currentAiThreadId}</span></div>
            <div className="flex justify-between"><span>Messages:</span> <span className="text-white">{messages.length}</span></div>
            <div className="flex justify-between"><span>Rate Limit:</span> <span className={rateLimit ? 'text-[var(--t-error)]' : 'text-[var(--t-success)]'}>{rateLimit ? 'ACTIVE' : 'READY'}</span></div>
            <div className="flex justify-between border-t border-[var(--t-border)] mt-2 pt-2">
              <button 
                onClick={() => {
                  localStorage.removeItem('ai_threads');
                  localStorage.removeItem('ai_messages_map');
                  window.location.reload();
                }}
                className="text-[var(--t-error)] hover:text-[var(--t-error-hover)] underline"
              >
                Factory Reset Threads
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reusable Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />

      <RateLimitModal
        isOpen={showRateLimitModal}
        onClose={() => setShowRateLimitModal(false)}
      />

    </div>
  );
}

